import { expandBlankLines } from "@caliu/utils";

/**
 * Re-attach a note's frontmatter block to a body produced by the editor.
 * Inverse of `splitFrontmatterDocument` (`@caliu/utils`): composing and
 * splitting must round-trip so the autosave pipeline never drops
 * frontmatter metadata. `contentRef` in the editor always holds this composed
 * FULL document — never a body-only string.
 */
export function composeFullContent(
  frontmatter: string | null,
  body: string,
): string {
  return frontmatter ? `---\n${frontmatter}\n---\n${body}` : body;
}

/**
 * Detect a note body's storage format and return the value to hand to Tiptap's
 * `setContent`. New notes are stored as Markdown (a string); legacy notes may
 * be stored as Tiptap/ProseMirror JSON (an object). A body that parses to a
 * JSON object is returned as that object; anything else — including a JSON
 * string/number/null, or non-JSON text — is treated as Markdown.
 *
 * Single source of truth shared by `NoteEditor` (live) and `ReadOnlyNoteEditor`
 * (history preview). The caller is responsible for stripping frontmatter first.
 */
export function parseEditorBody(body: string): object | string {
  try {
    const maybeJson = JSON.parse(body);
    // Must be a ProseMirror doc object, not a plain string/number.
    return maybeJson && typeof maybeJson === "object" ? maybeJson : body;
  } catch {
    return body; // Markdown body
  }
}

/**
 * Like {@link parseEditorBody}, but for handing markdown to the live/preview
 * editor. Runs blank-line expansion on markdown bodies so consecutive blank
 * lines survive the round-trip (see `expandBlankLines` and `BlankLineParagraph`).
 * Legacy Tiptap JSON objects pass through untouched. Every `setContent` call
 * site that loads a note body should use this, not `parseEditorBody` directly.
 */
export function parseEditorBodyForEditor(body: string): object | string {
  const parsed = parseEditorBody(body);
  return typeof parsed === "string" ? expandBlankLines(parsed) : parsed;
}
